// ─────────────────────────────────────────────────────────────────────────────
// Tokens — mesma linguagem visual dos dashboards novos (Produtividade, Ciclo)
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  bg: "#F5F2EA",
  surface: "#FFFFFF",
  border: "#E5E1D5",
  borderStrong: "#D6D1C2",
  text: "#1A1A1A",
  textMuted: "#5C5C5C",
  textSoft: "#8A8A82",

  brand: "#D04638",
  accent: "#E08A1E",
  info: "#2A75B0",
  success: "#2C8D5E",
  neutralSoft: "#ECEAE2",
};

// Uma cor por marketplace — a mesma nos cards, nas tabelas e nas barras
export const marketplaceColors = {
  2967: "#E08A1E", // Amazon DBA
  4410: "#2A75B0", // Mercado Livre Flex
  4412: "#5B8FC7", // Mercado Livre Padrão
  4041: "#D04638", // Shopee
  5440: "#E2725B", // Shopee Rápido
  4293: "#1A1A1A", // TikTok
};

export const fonts = {
  body: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'Geist Mono', 'SF Mono', Menlo, monospace",
};

export const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700;800&family=Geist+Mono:wght@400;500;700&display=swap');
`;

export const page = {
  minHeight: "100vh",
  background: colors.bg,
  fontFamily: fonts.body,
  color: colors.text,
  padding: "28px 32px 48px",
  boxSizing: "border-box",
};

export const header = {
  wrap: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 20,
    flexWrap: "wrap",
    marginBottom: 22,
  },
  title: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: colors.textMuted,
    marginTop: 4,
  },
  filters: {
    display: "flex",
    alignItems: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    fontSize: 10.5,
    fontWeight: 700,
    color: colors.textSoft,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  input: {
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: 9,
    padding: "9px 12px",
    fontSize: 13.5,
    fontWeight: 600,
    fontFamily: fonts.body,
    color: colors.text,
    background: colors.surface,
    outline: "none",
  },
  button: {
    border: "none",
    borderRadius: 9,
    padding: "11px 20px",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: fonts.body,
    background: colors.text,
    color: "#fff",
    cursor: "pointer",
  },
  buttonGhost: {
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: 9,
    padding: "11px 18px",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: fonts.body,
    background: colors.surface,
    color: colors.textMuted,
    cursor: "pointer",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
};

export const progress = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: "16px 18px",
    marginBottom: 20,
  },
  text: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.textMuted,
    marginBottom: 10,
  },
  bar: {
    height: 6,
    borderRadius: 3,
    background: colors.neutralSoft,
    overflow: "hidden",
  },
  fill: (percentage) => ({
    height: "100%",
    width: `${percentage}%`,
    background: colors.info,
    borderRadius: 3,
    transition: "width .2s",
  }),
};

export const notice = {
  error: {
    background: "#FBE5E2",
    border: `1px solid ${colors.brand}`,
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 600,
    color: colors.brand,
    marginBottom: 20,
  },
  empty: {
    background: colors.surface,
    border: `1px dashed ${colors.borderStrong}`,
    borderRadius: 12,
    padding: "56px 20px",
    textAlign: "center",
    fontSize: 14,
    color: colors.textMuted,
  },
};

export const kpis = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 12,
    marginBottom: 22,
  },
  card: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: "14px 16px",
  },
  label: {
    fontSize: 10.5,
    fontWeight: 700,
    color: colors.textSoft,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  value: {
    fontSize: 27,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    marginTop: 3,
    lineHeight: 1.1,
  },
  valueMoney: {
    fontSize: 22,
  },
  hint: {
    fontSize: 11.5,
    color: colors.textMuted,
    marginTop: 2,
  },
};

export const section = {
  // Uma tabela por linha, largura cheia. Lado a lado, os nomes de produto
  // (que passam de 60 caracteres) espremiam as colunas numéricas e a página
  // ganhava scroll horizontal.
  grid: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    marginBottom: 16,
  },
  card: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: "16px 18px",
    minWidth: 0,
  },
  title: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: colors.text,
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 11.5,
    color: colors.textMuted,
    marginBottom: 12,
  },
  // Título à esquerda, ações à direita
  cardHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  cardActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  actionButton: {
    border: `1px solid ${colors.borderStrong}`,
    borderRadius: 7,
    padding: "5px 11px",
    fontSize: 11,
    fontWeight: 700,
    fontFamily: fonts.body,
    background: colors.surface,
    color: colors.textMuted,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};

export const modal = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(26,26,26,.5)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 2000,
  },
  box: {
    background: colors.surface,
    borderRadius: 14,
    boxShadow: "0 24px 64px rgba(0,0,0,.28)",
    width: 820,
    maxWidth: "100%",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "18px 20px",
    fontFamily: fonts.body,
  },
  head: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexShrink: 0,
    marginBottom: 6,
  },
  // Rola por dentro: a lista completa pode ter centenas de SKUs
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  close: {
    border: "none",
    background: "transparent",
    color: colors.textMuted,
    fontSize: 22,
    lineHeight: 1,
    padding: "0 4px",
    cursor: "pointer",
    fontFamily: fonts.body,
  },
};

export const table = {
  base: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    // Sem isto a célula do produto (nome longo em nowrap) define a largura
    // intrínseca e estoura a tabela, gerando scroll horizontal na página.
    // Com layout fixo as colunas numéricas mandam e o nome corta com ellipsis.
    tableLayout: "fixed",
  },
  th: {
    textAlign: "left",
    fontSize: 10,
    fontWeight: 800,
    color: colors.textSoft,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "0 8px 7px",
    borderBottom: `1px solid ${colors.border}`,
  },
  thRight: {
    textAlign: "right",
    // Largura fixa nas colunas de número: o resto sobra pro nome do produto
    width: 130,
  },
  td: {
    padding: "8px",
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
  },
  tdRight: {
    textAlign: "right",
    fontWeight: 700,
    whiteSpace: "nowrap",
    fontFamily: fonts.mono,
  },
  tdMuted: {
    color: colors.textMuted,
  },
  rank: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: 6,
    background: colors.neutralSoft,
    fontSize: 10.5,
    fontWeight: 800,
    fontFamily: fonts.mono,
    marginRight: 8,
    flexShrink: 0,
  },
  // Célula do produto: rank + miniatura + nome, tudo na mesma linha
  skuCell: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    minWidth: 0,
  },
  skuThumb: {
    width: 34,
    height: 34,
    borderRadius: 7,
    objectFit: "contain",
    background: colors.neutralSoft,
    border: `1px solid ${colors.border}`,
    marginRight: 10,
    flexShrink: 0,
  },
  // Placeholder pro produto sem imagem — mantém o alinhamento das linhas
  skuThumbEmpty: {
    width: 34,
    height: 34,
    borderRadius: 7,
    background: colors.neutralSoft,
    border: `1px dashed ${colors.borderStrong}`,
    marginRight: 10,
    flexShrink: 0,
  },
  skuName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  // Barra de participação embaixo do rótulo — concentração sem gráfico
  shareBar: (percentage, color) => ({
    display: "block",
    height: 3,
    marginTop: 5,
    borderRadius: 2,
    background: colors.neutralSoft,
    position: "relative",
    overflow: "hidden",
    backgroundImage: `linear-gradient(${color}, ${color})`,
    backgroundSize: `${percentage}% 100%`,
    backgroundRepeat: "no-repeat",
  }),
  typeDot: (color) => ({
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: 3,
    background: color,
    marginRight: 8,
    flexShrink: 0,
  }),
};
